import { JOB_QUESTION_TYPES, QUESTION_TYPES } from '@/store';

export const GEOGRAPHIC_PREFERENCE = {
  NO_PREFERENCE: 'none',
  REGION: 'region',
  STATE: 'state',
  METROPOLITAN: 'metropolitan',
};

export const GEOGRAPHIC_PREFERENCE_OPTIONS_NAMES = {
  // [GEOGRAPHIC_PREFERENCE.NO_PREFERENCE]: 'No preference',
  [GEOGRAPHIC_PREFERENCE.REGION]: 'Region / Subregion',
  [GEOGRAPHIC_PREFERENCE.STATE]: 'State / City',
  [GEOGRAPHIC_PREFERENCE.METROPOLITAN]: 'Metropolitan Area',
};

export const defaultGeographyQuestion = {};

export const createGeographyQuestionObjectFromJobProfile = (
  jobProfile = null
) => {
  const {
    geographic_preference_type: type = GEOGRAPHIC_PREFERENCE.NO_PREFERENCE,
    geographic_preference_value: value = null,
    geographic_preference_is_required: required = 1,
  } = jobProfile || {};
  return {
    id: Date.now(),
    member_preamble: null,
    talent_preamble: null,
    member_prompt: 'For your ideal job, where would you like to work?',
    talent_prompt: null,
    member_explanation: null,
    talent_explanation: null,
    label: 'Desired Location',
    type: JOB_QUESTION_TYPES.GEOGRAPHY,
    talent_type: JOB_QUESTION_TYPES.GEOGRAPHY,
    required,
    is_active: 1,
    jobQuestionVariables: [
      {
        jobProfileQuestionResponses: [
          {
            type,
            value: value ? JSON.parse(value) : null,
          },
        ],
      },
    ],

    questions: [],
  };
};

export const getTypeAndValueFromGeographicQuestion = (
  geographicQuestion = null
) => {
  const defaultData = {
    type: GEOGRAPHIC_PREFERENCE.NO_PREFERENCE,
    value: null,
  };
  if (!geographicQuestion) return defaultData;
  const questionVariable = Array.isArray(
    geographicQuestion.jobQuestionVariables
  )
    ? geographicQuestion.jobQuestionVariables[0]
    : null;
  const geographicData =
    questionVariable &&
    Array.isArray(questionVariable.jobProfileQuestionResponses)
      ? questionVariable.jobProfileQuestionResponses[0]
      : null;
  if (!geographicData) {
    return defaultData;
  }
  return { ...defaultData, ...geographicData };
};

export const isGeographicPreferenceSpecified = (jobProfile = null) => {
  const {
    geographic_preference_type: type = null,
    geographic_preference_value: value = null,
  } = jobProfile || {};
  return type && value;
};

export const getJobQuestionResponseValues = (jobQuestion) => {
  let data;
  let result = [];
  if (!jobQuestion.required && isJobQuestionDeclined(jobQuestion)) {
    return ['No preference'];
  }
  switch (jobQuestion.type) {
    case JOB_QUESTION_TYPES.MULTI_VARIABLE_SELECT_ONE:
      return (jobQuestion.jobQuestionVariables || []).reduce(
        (acc, variable) => {
          if (
            !variable.jobProfileQuestionResponses ||
            !variable.jobProfileQuestionResponses[0] ||
            !variable.jobProfileQuestionResponses[0].value
          ) {
            return acc;
          }
          const optionId = parseInt(variable.jobProfileQuestionResponses[0].value);
          if (!optionId) {
            return acc;
          }
          const option = (variable.variableOptions || []).find(
            (o) => o.id === optionId
          );
          if (option) {
            acc.push(
              `<div class="d-flex justify-content-start">
                  <span>${variable.short_label || variable.input_label}: 
                    <i>${option.name}</i>
                  </span>
                </div>`
            );
          }
          return acc;
        },
        result
      );
    case JOB_QUESTION_TYPES.SELECT_ANY:
    case JOB_QUESTION_TYPES.SELECT_ONE:
      return (jobQuestion.jobQuestionVariables || []).reduce(
        (acc, variable) => {
          if (
            Array.isArray(variable.jobProfileQuestionResponses) &&
            variable.jobProfileQuestionResponses.some(
              (r) => parseInt(r.value) === 1
            )
          ) {
            acc.push(variable.short_label || variable.input_label);
          }
          return acc;
        },
        result
      );
    case JOB_QUESTION_TYPES.RATING:
    case JOB_QUESTION_TYPES.SLIDER:
      // eslint-disable-next-line no-case-declarations
      const isVariableSinge = (jobQuestion.jobQuestionVariables || []).length === 1;
      return (jobQuestion.jobQuestionVariables || []).reduce(
        (acc, variable) => {
          const res = (variable.jobProfileQuestionResponses || []).find(
            (r) => r.value !== null
          );
          if (res) {
            const nf = new Intl.NumberFormat('en-US');
            const value = nf.format(res.value);
            let variableLabel = `<div>${
              variable.short_label || variable.input_label
            }:</div>&nbsp;`;
            if (isVariableSinge) {
              variableLabel = '';
            }
            acc.push(
              `<div class="d-flex">${variableLabel}
                <div>
                   ${variable.prefix || ''}${value}${variable.suffix || ''}
                </div>
              </div>`
            );
          }
          return acc;
        },
        result
      );
    case JOB_QUESTION_TYPES.YES_NO:
      return (jobQuestion.jobQuestionVariables || []).reduce(
        (acc, variable) => {
          const res = (variable.jobProfileQuestionResponses || []).find(
            (r) => parseInt(r.value) === 0 || parseInt(r.value) === 1
          );
          if (res) {
            acc.push(parseInt(res.value) === 1 ? 'Yes' : 'No');
          }
          return acc;
        },
        result
      );
    case JOB_QUESTION_TYPES.GEOGRAPHY:
      data = getTypeAndValueFromGeographicQuestion(jobQuestion);
      if (data && data.value && data.value.display_name) {
        result.push(data.value.display_name);
      }
      return result;
    default:
      return result;
  }
};

export const isJobQuestionAnswered = (jobQuestion) => {
  const answeredValues = getJobQuestionResponseValues(jobQuestion).length;
  if (
    (jobQuestion.type === QUESTION_TYPES.MULTI_VARIABLE_SELECT_ONE ||
      jobQuestion.type === QUESTION_TYPES.RATING) &&
    jobQuestion.required
  ) {
    return answeredValues === jobQuestion.jobQuestionVariables.length;
  }
  return !!answeredValues;
};

export const isJobQuestionDeclined = (jq) =>
  (
    (((jq.jobQuestionVariables || [])[0] || {}).jobProfileQuestionResponses ||
      [])[0] || {}
  ).declined === 1;

export const createJobQuestionsSorter =
  (orderKey = 'id') =>
  (question1, question2) => {
    let numA = 0;
    let numB = 0;
    numA += question1.required
      ? question1.type === JOB_QUESTION_TYPES.GEOGRAPHY
        ? 100
        : 1000
      : 0;
    numB += question2.required
      ? question2.type === JOB_QUESTION_TYPES.GEOGRAPHY
        ? 100
        : 1000
      : 0;
    if (isJobQuestionDeclined(question1)) {
      numA -= 10;
    } else if (isJobQuestionAnswered(question1)) {
      numA += 10;
    }
    if (isJobQuestionDeclined(question2)) {
      numB -= 10;
    } else if (isJobQuestionAnswered(question2)) {
      numB += 10;
    }
    if (question1[orderKey] > question2[orderKey]) {
      numB += 5;
    } else {
      numA += 5;
    }
    return numB - numA;
  };

export const formatJobPreferenceVarValue = (variable, value) => {
  const nf = new Intl.NumberFormat('en-US');
  const prefix = variable.prefix || '';
  const suffix = variable.suffix || '';
  const formattedValue = nf.format(value);
  return `${prefix}${formattedValue}${suffix}`;
};
